"use client";

import { useEffect, useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Xmark } from "iconoir-react";

interface TourFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  googleFormUrl: string;
}

export const TourFormModal = ({
  isOpen,
  onClose,
  googleFormUrl,
}: TourFormModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // GSAP animation for modal entrance and exit
  useGSAP(
    () => {
      if (!modalRef.current || !backdropRef.current || !contentRef.current)
        return;

      if (isOpen) {
        // Entrance animation
        const tl = gsap.timeline();

        tl.set(modalRef.current, { display: "flex" })
          .fromTo(
            backdropRef.current,
            { opacity: 0 },
            { opacity: 1, duration: 0.3, ease: "power2.out" },
          )
          .fromTo(
            contentRef.current,
            { opacity: 0, scale: 0.9, y: 20 },
            {
              opacity: 1,
              scale: 1,
              y: 0,
              duration: 0.4,
              ease: "power3.out",
            },
            "-=0.1",
          )
          .fromTo(
            closeButtonRef.current,
            { opacity: 0, scale: 0.8 },
            { opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.7)" },
            "-=0.2",
          );
      } else {
        // Exit animation
        const tl = gsap.timeline({
          onComplete: () => {
            if (modalRef.current) {
              gsap.set(modalRef.current, { display: "none" });
            }
          },
        });

        tl.to(contentRef.current, {
          opacity: 0,
          scale: 0.9,
          y: 20,
          duration: 0.25,
          ease: "power2.in",
        }).to(
          backdropRef.current,
          { opacity: 0, duration: 0.2, ease: "power2.in" },
          "-=0.1",
        );
      }
    },
    { dependencies: [isOpen], revertOnUpdate: true },
  );

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 hidden items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop with blur effect */}
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-stone-950/60 backdrop-blur-md"
      />

      {/* Modal wrapper for positioning close button above */}
      <div className="relative z-10 w-full max-w-[640px] flex flex-col items-end">
        {/* Close button - positioned outside and above the modal */}
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="mb-3 w-10 h-10 flex items-center justify-center bg-stone-950 rounded-full text-surface hover:bg-stone-800 transition-colors duration-200 shadow-lg"
          aria-label="Close modal"
        >
          <Xmark className="w-5 h-5" strokeWidth={2.5} />
        </button>

        {/* Modal content container */}
        <div
          ref={contentRef}
          className="w-full max-h-[90vh] bg-surface rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Google Forms iframe */}
          <div className="w-full h-[90vh] max-h-[90vh]">
            <iframe
              src={googleFormUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              marginHeight={0}
              marginWidth={0}
              className="w-full h-full"
              title="Form"
            >
              Loading…
            </iframe>
          </div>
        </div>
      </div>
    </div>
  );
};
